import React, { useEffect, useRef, useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Chip, 
  IconButton,
  Divider,
  CircularProgress
} from '@mui/material';
import { 
  ArrowBack,
  Download,
  ZoomIn,
  ZoomOut,
  Fullscreen,
  FullscreenExit
} from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Header from '../components/common/Header';

const PDFViewer = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // État Redux
  const { user } = useSelector(state => state.auth);
  
  // État local
  const [product, setProduct] = useState(location.state?.product || null);
  const [loading, setLoading] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const viewerRef = useRef(null);

  // Détecter si on est sur mobile
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isSmallScreen = window.innerWidth < 768;
      setIsMobile(isMobileDevice || isSmallScreen);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // Si on n'a pas le produit dans le state de navigation, le récupérer
    if (!product && productId) {
      // TODO: Récupérer le produit par ID si nécessaire
      console.log('Récupération du produit', productId);
    }
  }, [productId, product]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.2, 3));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.2, 0.6));
  };

  const handleFullscreenToggle = async () => {
    if (!viewerRef.current) return;

    try {
      if (!document.fullscreenElement) {
        await viewerRef.current.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.error('Erreur lors du passage en plein écran:', error);
      toast.error('Impossible d\'activer le mode plein écran');
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleOpenInBrowser = () => {
    if (product?.pdf_url) {
      window.open(product.pdf_url, '_blank');
    }
  };

  const handleShare = () => {
    if (navigator.share && product) {
      navigator.share({
        title: product.title,
        text: product.description,
        url: window.location.href,
      }).catch(console.error);
    } else {
      // Fallback: copier l'URL dans le presse-papiers
      navigator.clipboard.writeText(window.location.href).then(() => {
        toast.success('Lien copié dans le presse-papiers !');
      }).catch(() => {
        toast.error('Impossible de copier le lien');
      });
    }
  };

  if (!product) {
    return (
      <Box className="mobile-app-container has-fixed-header" sx={{ bgcolor: "#FFF7E5", minHeight: '100vh' }}>
        <Header />
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="h6" color="error">
            Produit non trouvé
          </Typography>
          <Button onClick={handleGoBack} sx={{ mt: 2 }}>
            Retour
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="mobile-app-container has-fixed-header" sx={{ bgcolor: "#FFF7E5", minHeight: '100vh' }}>
      <Header />
      
      <Box sx={{ p: 2 }}>
        {/* En-tête avec bouton retour */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={handleGoBack} sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontFamily: 'Achiko' }}>
            {product.title}
          </Typography>
         
        </Box>

        {/* Badges */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Chip 
            label="PDF" 
            sx={{ 
              bgcolor: '#2196F3', 
              color: 'white',
              fontWeight: 'bold'
            }} 
          />
          {product.is_free ? (
            <Chip 
              label="GRATUIT" 
              sx={{ 
                bgcolor: '#4CAF50', 
                color: 'white',
                fontWeight: 'bold'
              }} 
            />
          ) : (
            <Chip 
              label="PREMIUM" 
              sx={{ 
                bgcolor: '#FF9800', 
                color: 'white',
                fontWeight: 'bold'
              }} 
            />
          )}
        </Box>

        {/* Message spécial pour mobile */}
       

        {/* Viewer PDF */}
        <Paper 
          elevation={3} 
          sx={{ 
            height: isMobile ? '400px' : 'calc(100vh - 200px)',
            overflow: 'hidden',
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {!isMobile && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 1, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
              <IconButton onClick={handleZoomOut} disabled={zoom <= 0.6} size="small">
                <ZoomOut />
              </IconButton>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
                {(zoom * 100).toFixed(0)}%
              </Typography>
              <IconButton onClick={handleZoomIn} disabled={zoom >= 3} size="small">
                <ZoomIn />
              </IconButton>
              <IconButton onClick={handleFullscreenToggle} size="small">
                {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
              </IconButton>
            </Box>
          )}

          <Box
            ref={viewerRef}
            sx={{
              flex: 1,
              overflow: 'auto',
              backgroundColor: '#f5f5f5',
              position: 'relative'
            }}
          >
            {product.pdf_url ? (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  minHeight: isMobile ? '350px' : '600px',
                  position: 'relative'
                }}
              >
                {isMobile ? (
                  // Sur mobile, affichage simplifié avec message d'encouragement
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 3,
                      textAlign: 'center'
                    }}
                  >
                    <Box
                      component="img"
                      src="/pdf-icon.png"
                      alt="PDF"
                      sx={{
                        width: 80,
                        height: 80,
                        mb: 2,
                        opacity: 0.7
                      }}
                      onError={(e) => {
                        // Fallback si l'image n'existe pas
                        e.target.style.display = 'none';
                      }}
                    />
                    <Typography variant="h6" gutterBottom sx={{ color: '#1976d2' }}>
                      📄 {product.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      PDF prêt à être consulté
                    </Typography>
                    
                    {/* Aperçu avec iframe en taille réduite */}
                    <Box
                      sx={{
                        width: '100%',
                        height: '200px',
                        border: '2px solid #e0e0e0',
                        borderRadius: 2,
                        overflow: 'hidden',
                        mb: 2,
                        position: 'relative'
                      }}
                    >
                      <iframe
                        src={`${product.pdf_url}#toolbar=0&navpanes=0&scrollbar=0&page=1&view=FitH`}
                        width="100%"
                        height="100%"
                        style={{ border: 'none', pointerEvents: 'none' }}
                        title={`Aperçu PDF - ${product.title}`}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(0,0,0,0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer'
                        }}
                        onClick={handleOpenInBrowser}
                      >
                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>
                          Cliquer pour ouvrir
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  // Sur desktop, affichage normal avec zoom
                  <object
                    data={`${product.pdf_url}#toolbar=1&navpanes=1&scrollbar=1&page=1&view=FitH`}
                    type="application/pdf"
                    width="100%"
                    height="100%"
                    style={{ 
                      border: 'none',
                      transform: `scale(${zoom})`,
                      transformOrigin: 'top left',
                      width: `${100 / zoom}%`,
                      height: `${100 / zoom}%`
                    }}
                  >
                    <iframe
                      src={`${product.pdf_url}#toolbar=1&navpanes=1&scrollbar=1&page=1&view=FitH`}
                      width="100%"
                      height="100%"
                      style={{ border: 'none' }}
                      title={`PDF - ${product.title}`}
                    />
                    
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="body1" gutterBottom>
                        Votre navigateur ne peut pas afficher ce PDF.
                      </Typography>
                      <Button
                        variant="contained"
                        href={product.pdf_url}
                        target="_blank"
                        download
                        sx={{ mt: 1 }}
                      >
                        Télécharger le PDF
                      </Button>
                    </Box>
                  </object>
                )}
              </Box>
            ) : (
              <Box 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  flexDirection: 'column',
                  color: 'white'
                }}
              >
                <Typography variant="h6" color="error" gutterBottom>
                  PDF non disponible
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Le fichier PDF n'a pas pu être chargé
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Description */}
        {product.description && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom sx={{ fontFamily: 'Achiko' }}>
              Description
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {product.description}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PDFViewer;
